import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Image as ImageIcon, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { compressImage } from "../lib/imageCompress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FINISH_OPTIONS = {
  "Vinyl Wrapping": ["Gloss", "Satin", "Matte", "Chrome", "Brushed Metal", "Carbon", "Other / Not Sure"],
  "Coloured PPF": ["Matte Black", "Satin", "Gloss", "Chrome", "Other / Not Sure"],
  "Paint Protection Film (PPF)": ["Full Body", "Front End (PPF Bra)", "High-Impact Zones", "Custom — Tell Us"],
};

const FINISH_LABEL = {
  "Vinyl Wrapping": "Finish Preference",
  "Coloured PPF": "Colour / Finish",
  "Paint Protection Film (PPF)": "Coverage Area",
};

export const QuoteDialog = ({ service, open, onClose }) => {
  const empty = {
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    finish_preference: "",
    notes: "",
  };
  const [f, setF] = useState(empty);
  const [photos, setPhotos] = useState([]); // data URLs
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setF(empty);
      setPhotos([]);
      setDone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const room = 8 - photos.length;
    const take = files.slice(0, room);
    if (files.length > room) toast.warning(`Max 8 photos — added ${room}.`);
    const out = [];
    for (const file of take) {
      const dataUrl = await compressImage(file, { maxDim: 2000, quality: 0.8 });
      if (!dataUrl) continue;
      out.push(dataUrl);
    }
    setPhotos((p) => [...p, ...out]);
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email) || !f.phone.trim() || !f.vehicle.trim()) {
      toast.error("Name, email, phone and vehicle are required.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: f.name,
        email: f.email,
        phone: f.phone,
        service_type: service,
        vehicle: f.vehicle,
        finish_preference: f.finish_preference,
        coverage: "",
        notes: f.notes,
        photos,
      });
      setDone(true);
      toast.success("Quote request sent.");
    } catch (err) {
      toast.error("Couldn't send. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  const finishLabel = FINISH_LABEL[service] || "Finish Preference";
  const options = FINISH_OPTIONS[service] || ["Not Sure"];

  const inputCls =
    "w-full bg-transparent border border-[#27272A] focus:border-[#DC2626] text-white py-2.5 px-3 placeholder-zinc-600 focus:outline-none text-sm";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="quote-dialog"
        className="max-w-2xl bg-[#0F0F0F] border-[#27272A] text-white rounded-none p-0 max-h-[90vh] overflow-y-auto"
        aria-describedby="quote-desc"
      >
        <VisuallyHidden.Root>
          <DialogTitle>Request a quote</DialogTitle>
          <DialogDescription id="quote-desc">
            Quote request form for {service}.
          </DialogDescription>
        </VisuallyHidden.Root>

        <div className="p-6 border-b border-[#27272A] flex items-start justify-between">
          <div>
            <div className="font-mono-accent text-[10px] text-[#DC2626] mb-2">QUOTE REQUEST</div>
            <h2 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tighter">{service}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <CheckCircle2 className="h-14 w-14 text-[#DC2626] mx-auto mb-5" />
            <h3 className="font-display font-bold uppercase text-2xl mb-2">Got it.</h3>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              We'll review your photos and call you back with a tailored quote.
            </p>
            <button
              onClick={onClose}
              className="bg-[#DC2626] hover:bg-[#EF4444] text-white font-display font-bold uppercase tracking-wider text-sm px-6 py-3"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DField label="Full Name *">
                <input data-testid="quote-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} />
              </DField>
              <DField label="Phone *">
                <input data-testid="quote-phone" type="tel" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
              </DField>
              <DField label="Email *">
                <input data-testid="quote-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} />
              </DField>
              <DField label="Vehicle (Make / Model / Year) *">
                <input data-testid="quote-vehicle" className={inputCls} value={f.vehicle} onChange={(e) => set("vehicle", e.target.value)} placeholder="e.g. Audi RS6 2024" />
              </DField>
              <div className="md:col-span-2">
                <DField label={finishLabel}>
                  <div className="flex flex-wrap gap-2">
                    {options.map((o) => (
                      <button
                        key={o}
                        type="button"
                        data-testid={`quote-finish-${o.replace(/\s/g, "-").toLowerCase()}`}
                        onClick={() => set("finish_preference", o)}
                        className={`font-mono-accent text-[10px] border px-3 py-2 transition-colors ${
                          f.finish_preference === o
                            ? "border-[#DC2626] bg-[#DC2626]/10 text-white"
                            : "border-[#27272A] text-zinc-400 hover:text-white"
                        }`}
                      >
                        {o.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </DField>
              </div>
              <div className="md:col-span-2">
                <DField label="Anything else? (Optional)">
                  <textarea
                    data-testid="quote-notes"
                    rows={2}
                    className={`${inputCls} resize-none`}
                    value={f.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Timeframes, budget, specific panels, anything we should know."
                  />
                </DField>
              </div>
              <div className="md:col-span-2">
                <DField label={`Photos of your car (up to 8 · helps us quote accurately)`}>
                  <label className="relative flex flex-col items-center justify-center border-2 border-dashed border-[#27272A] hover:border-[#DC2626] bg-[#0A0A0A] py-6 cursor-pointer transition-colors">
                    <Upload className="h-5 w-5 text-[#DC2626] mb-2" />
                    <span className="font-mono-accent text-[10px] text-zinc-400">
                      CLICK OR DROP · {photos.length}/8 ADDED
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onFiles}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      data-testid="quote-photo-input"
                    />
                  </label>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3">
                      {photos.map((src, i) => (
                        <div key={i} className="relative aspect-square border border-[#27272A] overflow-hidden group">
                          <img src={src} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            className="absolute top-1 right-1 bg-black/70 hover:bg-[#DC2626] text-white p-1 opacity-0 group-hover:opacity-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </DField>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-5 py-3 font-mono-accent text-xs border border-[#27272A] text-zinc-300 hover:border-white">
                CANCEL
              </button>
              <button
                type="submit"
                data-testid="quote-submit"
                disabled={busy}
                className="px-6 py-3 font-display font-bold uppercase tracking-wider text-sm bg-[#DC2626] hover:bg-[#EF4444] text-white disabled:opacity-60 inline-flex items-center gap-2"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy ? "Sending..." : "Request Quote"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DField = ({ label, children }) => (
  <div>
    <label className="font-mono-accent text-[10px] text-zinc-500 mb-1.5 block">{label}</label>
    {children}
  </div>
);
